<template lang="pug">
div
  v-menu.index(v-model="openMenu" :close-on-content-click="false" :top="positionTop" :attach="attach" rounded="lg")
    template(v-slot:activator='{ on }')
      div.text-sm.font-semibold.neutral700--text.underline.clickable(v-on="on") {{ $t('add_adjustment') }}
    v-card.white
      v-card-text
        .text-xs.font-normal.neutral700--text.my-2 {{ $t('add_or_deduct_from_amount') }}
        v-row(dense)
          v-col
            v-btn.button-xs(outlined block :class="adjustmentCategory === 'add' ? '' : 'border-not-selected'" @click="onSelectAdjustmentCategory('add')")
              |
              .font-medium {{ $t('add') }}
          v-col
            v-btn.button-xs(outlined block :class="adjustmentCategory === 'deduct' ? '' : 'border-not-selected'" @click="onSelectAdjustmentCategory('deduct')")
              |
              .font-medium {{ $t('deduct') }}

        .text-xs.font-normal.neutral700--text.my-2 {{ $t('choose_type') }}
        v-row(dense)
          v-col
            v-btn.button-xs(outlined block style="height: 50px !important;" :class="adjustmentType === 'currency' ? '' : 'border-not-selected'" @click="onSelectAdjustmentType('currency')")
              .d-flex.flex-column
                .text-xs.font-medium {{ $currency.symbol }}
                .text-xxs.font-normal {{ $t('fixed_amount') }}
          v-col
            v-btn.button-xs(outlined block style="height: 50px !important;" :class="adjustmentType === 'percentage' ? '' : 'border-not-selected'" @click="onSelectAdjustmentType('percentage')")
              .d-flex.flex-column
                .text-xs.font-bold %
                .text-xxs.font-normal {{ $t('percentage') }}

        .text-xs.font-normal.neutral700--text.mt-2 {{ $t('enter_adjustment_amount') }}
        div.mt-2
          v-form(ref="form")
            div.d-flex(v-if="adjustmentType === 'currency'")
              v-text-field.text-sm.font-normal.round-xl(v-model="adjustmentAmount" placeholder="0.00" type="number" :prefix="$currency.symbol" background-color="basic100" outlined :rules="rules.has_quantity" dense hide-details="auto")
              v-card.round-lg.calculator.neutral300.px-2.ml-2(outlined link height="40" @click="showCalculator = true")
                v-icon(color="neutral700" size="24") $yzi-calculator
            v-text-field.text-sm.font-normal.round-xl(v-else v-model="adjustmentAmount" placeholder="0" type="number" suffix="%" background-color="basic100" outlined :rules="rules.has_quantity" dense hide-details="auto")

          .d-flex.align-center.mt-4
            yz-checkbox(v-model="isAdjustmentTaxable" label="Adjustment is taxable" label-gap="8px")

          //- Adjustment calculator
          adjustment-calculator(v-if="showCalculator" :show.sync="showCalculator" :invoice="invoice" @apply="onApplyCalculator")

        .d-flex.justify-end.mt-4
          v-btn.button-sm(color="primary" depressed :loading="isLoading" @click="onConfirm") {{ $t('confirm') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AdjustmentCalculator from '@/components/clinic/checkout/AdjustmentCalculator.vue'

export default {
  components: { AdjustmentCalculator },
  props: {
    totalAmount: {
      type: Number,
      required: true,
      default: null
    },
    invoice: {
      type: Object,
      default: null
    },
    positionTop: {
      type: Boolean,
      default: false
    },
    attach: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading: false,
      adjustmentCategory: 'add',
      adjustmentType: 'currency',
      adjustmentAmount: null,
      isAdjustmentTaxable: true,
      openMenu: false,
      showCalculator: false,
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        not_empty_select: [v => !!v || 'This field is required']
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    leadPid() {
      return this.$route.params.pid
    },
    taxRate() {
      if (!this.invoice || !this.invoice.extra_data || !this.invoice.extra_data.tax_rate) {
        return 0
      }
      return this.invoice.extra_data.tax_rate.rate || 0
    },
    payload() {
      let discountAmount
      if (this.adjustmentType === 'currency') {
        discountAmount = Math.round(this.adjustmentAmount * this.$currency.multiplier)
      }
      if (this.adjustmentType === 'percentage') {
        discountAmount = Math.round((this.adjustmentAmount / 100) * this.totalAmount)
      }
      if (this.adjustmentCategory === 'add') {
        discountAmount = -discountAmount
      }
      return {
        invoice_discount: discountAmount,
        extra_data: {
          ...this.invoice?.extra_data,
          is_adjustment_taxable: this.isAdjustmentTaxable
        }
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    onSelectAdjustmentCategory(category) {
      this.adjustmentCategory = category
    },
    onSelectAdjustmentType(type) {
      this.adjustmentType = type
    },
    onApplyCalculator({ adjustment, isAdjustmentTaxable }) {
      // Set adjustment type to currency
      this.adjustmentType = 'currency'

      // Set adjustment amount (convert from cents to currency)
      this.adjustmentAmount = Math.abs(adjustment) / this.$currency.multiplier

      // Set category based on sign (positive = deduct, negative = add)
      this.adjustmentCategory = adjustment >= 0 ? 'deduct' : 'add'

      // Set taxable flag from calculator
      this.isAdjustmentTaxable = isAdjustmentTaxable

      // Close calculator
      this.showCalculator = false
    },
    async onConfirm() {
      if (!this.$refs.form.validate()) {
        return
      }
      try {
        this.isLoading = true
        const endpoint = `/invoices/${this.leadPid}`
        const resp = await this.$axios.patch(endpoint, this.payload)
        this.updateOldActivityLog(endpoint, this.payload, resp.data, 'PATCH', null)
        const amountDisplay = this.adjustmentType === 'currency' ? `${this.$currency.symbol}${this.adjustmentAmount}` : `${this.adjustmentAmount}%`
        const activity = `${this.adjustmentCategory === 'add' ? 'Added' : 'Deducted'} adjustment ${amountDisplay} (${this.isAdjustmentTaxable ? 'taxable' : 'non-taxable'})`
        this.createActivityLog('add_adjustment', activity)
        this.$emit('updated')
        this.showMessage(['success', this.$t('success_message.update_successful')])
        this.openMenu = false
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    updateOldActivityLog(endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'invoice',
        activity: `Update invoice (#${response.rid})`,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data).catch(() => {})
    },
    createActivityLog(tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: 'checkout',
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
.border-not-selected {
  border: 1px solid #c1c1c1;
}
.index {
  z-index: 20;
}
.calculator {
  border-color: var(--v-neutral400) !important;
  align-content: center;
}
</style>
